import { browserLocalAuthorRepository } from "./local-author-repository.js";
import { browserLocalPrivacyControlsRepository } from "./privacy-controls.js";
import {
  LEGACY_CLIENT_V1_IMPORT_VERSION,
  createLegacyClientAuthorStageV1,
  createLegacyClientImportReceiptV1,
  createLegacyClientQuarantineV1,
  decodeLegacyClientAuthorStageV1,
  decodeLegacyClientImportReceiptV1,
  decodeLegacyClientQuarantineV1,
  decodeLegacyClientV1,
  isCanonicalLegacyClientUtcOrEmpty,
  isValidLegacyClientImportRevision,
  legacyClientAuthorsEqual,
  legacyClientImportReceiptProjection,
} from "./legacy-client-v1-import-contract.js";

const LEGACY_CLIENT_KEY = "fishystuff.client.v1";
const LEGACY_TAB_KEY = "fishystuff.client.session.v1";
const AUTHOR_STAGE_KEY = "fishystuff.client-v1-author-stage.v1";
const RECEIPT_KEY = "fishystuff.client-v1-import-receipt.v1";
const QUARANTINE_KEY = "fishystuff.client-v1-quarantine.v1";
const CROSS_CONTEXT_LOCK_NAME = "fishystuff.legacy-client-v1-import.v1";
const BROWSER_IMPORT_PROPERTY = "__fishystuffLegacyClientV1Import";
const BROWSER_IMPORT_BRAND = Symbol.for("fishystuff.legacy-client-v1-import.v1");

function invalid(reason) {
  return { kind: "invalid", reason };
}

function unsupportedVersion(foundVersion) {
  return { kind: "unsupportedVersion", foundVersion };
}

function storageFailure(operation, reason) {
  return { kind: "unavailable", operation, reason };
}

function validReadyState(state) {
  return state?.kind === "ready" && isValidLegacyClientImportRevision(state.revision);
}

function consentImportSettled(choice, state) {
  if (choice === "unset" || choice === "enabled") {
    return state?.kind === "missing" || state?.kind === "ready";
  }
  return state?.kind === "ready"
    && state.telemetryConsent?.decision?.kind === "denied";
}

function importedAuthorMatchesStage(state, author) {
  return validReadyState(state)
    && state.authorId === author.authorId
    && (
      author.displayName !== ""
        ? state.displayName === author.displayName
        : typeof state.displayName === "string" && state.displayName.length > 0
    )
    && (
      author.handle !== ""
        ? state.handle === author.handle
        : typeof state.handle === "string" && state.handle.length > 0
    );
}

export function createLegacyClientV1Importer({
  localStorage,
  sessionStorage,
  importLegacyAuthor,
  readConsentState,
  importLegacyConsentWithdrawal,
  lockManager = null,
  nowUtc = () => new Date().toISOString(),
} = {}) {
  if (typeof importLegacyAuthor !== "function") {
    throw new TypeError("Legacy author import requires one domain import command.");
  }
  if (
    typeof readConsentState !== "function"
    || typeof importLegacyConsentWithdrawal !== "function"
  ) {
    throw new TypeError("Legacy consent import requires its state and withdrawal commands.");
  }
  if (typeof nowUtc !== "function") throw new TypeError("nowUtc must be a function.");

  let currentState = Object.freeze({ kind: "uninitialized" });
  let commonPending = null;
  let authorMutationTail = Promise.resolve();

  function state() {
    return Object.freeze({ ...currentState });
  }

  function publish(nextState) {
    currentState = Object.freeze({ ...nextState });
    return state();
  }

  async function withCrossContextLock(operation) {
    if (!lockManager || typeof lockManager.request !== "function") {
      return publish(storageFailure("lock", "cross-context-lock-unavailable"));
    }

    let entered = false;
    try {
      const result = await lockManager.request(
        CROSS_CONTEXT_LOCK_NAME,
        { mode: "exclusive" },
        async () => {
          entered = true;
          return operation();
        },
      );
      if (!entered) {
        return publish(storageFailure("lock", "cross-context-lock-not-acquired"));
      }
      return result;
    } catch (_error) {
      if (entered) throw _error;
      return publish(storageFailure("lock", "cross-context-lock-request-failed"));
    }
  }

  function read(storage, key, reason) {
    try {
      if (!storage || typeof storage.getItem !== "function") {
        throw new TypeError("Storage is unavailable.");
      }
      return { ok: true, raw: storage.getItem(key) };
    } catch (_error) {
      return { ok: false, state: storageFailure("read", reason) };
    }
  }

  function write(storage, key, value, reason) {
    try {
      if (!storage || typeof storage.setItem !== "function") {
        throw new TypeError("Storage is unavailable.");
      }
      storage.setItem(key, JSON.stringify(value));
      return null;
    } catch (_error) {
      return storageFailure("write", reason);
    }
  }

  function remove(storage, key, reason) {
    try {
      if (!storage || typeof storage.removeItem !== "function") {
        throw new TypeError("Storage is unavailable.");
      }
      storage.removeItem(key);
      return null;
    } catch (_error) {
      return storageFailure("remove", reason);
    }
  }

  function timestamp(reason) {
    let value;
    try {
      value = nowUtc();
    } catch (_error) {
      return { ok: false, state: storageFailure("write", `${reason}-clock-failed`) };
    }
    if (!isCanonicalLegacyClientUtcOrEmpty(value) || value === "") {
      return { ok: false, state: storageFailure("write", `${reason}-clock-invalid`) };
    }
    return { ok: true, value };
  }

  function cleanupBroadLegacy() {
    return remove(localStorage, LEGACY_CLIENT_KEY, "legacy-client-remove-failed")
      || remove(sessionStorage, LEGACY_TAB_KEY, "legacy-tab-remove-failed");
  }

  function validateReceiptArtifacts(receipt) {
    const stageRead = read(localStorage, AUTHOR_STAGE_KEY, "author-stage-read-failed");
    if (!stageRead.ok) return stageRead.state;
    if (receipt.author.kind === "staged") {
      if (stageRead.raw === null) return invalid("acknowledged-author-stage-is-missing");
      const stage = decodeLegacyClientAuthorStageV1(stageRead.raw);
      if (stage.kind !== "ready") return stage;
    } else if (stageRead.raw !== null) {
      const removeFailure = remove(
        localStorage,
        AUTHOR_STAGE_KEY,
        "author-stage-remove-failed",
      );
      if (removeFailure) return removeFailure;
    }

    if (receipt.result.kind === "requiresReconsent" || receipt.result.kind === "quarantined") {
      const quarantineRead = read(
        localStorage,
        QUARANTINE_KEY,
        "legacy-quarantine-read-failed",
      );
      if (!quarantineRead.ok) return quarantineRead.state;
      if (quarantineRead.raw === null) return invalid("acknowledged-quarantine-is-missing");
      const quarantine = decodeLegacyClientQuarantineV1(quarantineRead.raw);
      if (quarantine.kind !== "ready") return quarantine;
      const expectedSourceState = receipt.result.kind === "quarantined"
        ? receipt.result.sourceState
        : {
          kind: "requiresReconsent",
          reason: "legacy-enabled-has-no-versioned-consent",
        };
      if (
        JSON.stringify(quarantine.quarantine.sourceState)
        !== JSON.stringify(expectedSourceState)
      ) {
        return invalid("acknowledged-quarantine-does-not-match-receipt");
      }
    }
    return null;
  }

  function ensureAuthorStage(author, stagedAtUtc) {
    const existing = read(localStorage, AUTHOR_STAGE_KEY, "author-stage-read-failed");
    if (!existing.ok) return existing.state;
    if (existing.raw !== null) {
      const decoded = decodeLegacyClientAuthorStageV1(existing.raw);
      if (decoded.kind !== "ready") return decoded;
      if (!legacyClientAuthorsEqual(decoded.stage.author, author)) {
        return { kind: "conflict", reason: "legacy-author-stage-conflicts-with-source" };
      }
      return null;
    }
    return write(
      localStorage,
      AUTHOR_STAGE_KEY,
      createLegacyClientAuthorStageV1(author, stagedAtUtc),
      "author-stage-write-failed",
    );
  }

  function ensureQuarantine(raw, sourceState, quarantinedAtUtc) {
    const existing = read(localStorage, QUARANTINE_KEY, "legacy-quarantine-read-failed");
    if (!existing.ok) return existing.state;
    if (existing.raw !== null) {
      const decoded = decodeLegacyClientQuarantineV1(existing.raw);
      if (decoded.kind !== "ready") return decoded;
      if (
        decoded.quarantine.raw !== raw
        || JSON.stringify(decoded.quarantine.sourceState) !== JSON.stringify(sourceState)
      ) {
        return { kind: "conflict", reason: "legacy-quarantine-conflicts-with-source" };
      }
      return null;
    }
    return write(
      localStorage,
      QUARANTINE_KEY,
      createLegacyClientQuarantineV1(raw, sourceState, quarantinedAtUtc),
      "legacy-quarantine-write-failed",
    );
  }

  function writeReceipt(author, result, recordedAtUtc, reason) {
    return write(
      localStorage,
      RECEIPT_KEY,
      createLegacyClientImportReceiptV1(author, result, recordedAtUtc),
      reason,
    );
  }

  async function runCommon() {
    const receiptRead = read(localStorage, RECEIPT_KEY, "import-receipt-read-failed");
    if (!receiptRead.ok) return publish(receiptRead.state);
    if (receiptRead.raw !== null) {
      const receipt = decodeLegacyClientImportReceiptV1(receiptRead.raw);
      if (receipt.kind !== "ready") return publish(receipt);
      const artifactFailure = validateReceiptArtifacts(receipt.receipt);
      if (artifactFailure) return publish(artifactFailure);
      const cleanupFailure = cleanupBroadLegacy();
      return publish(cleanupFailure || legacyClientImportReceiptProjection(receipt.receipt));
    }

    const sourceRead = read(localStorage, LEGACY_CLIENT_KEY, "legacy-client-read-failed");
    if (!sourceRead.ok) return publish(sourceRead.state);
    if (sourceRead.raw === null) {
      const stageRead = read(localStorage, AUTHOR_STAGE_KEY, "author-stage-read-failed");
      if (!stageRead.ok) return publish(stageRead.state);
      if (stageRead.raw !== null) return publish(invalid("unacknowledged-author-stage"));
      const cleanupFailure = remove(
        sessionStorage,
        LEGACY_TAB_KEY,
        "legacy-tab-remove-failed",
      );
      return publish(cleanupFailure || { kind: "missing" });
    }

    const recordedAt = timestamp("import");
    if (!recordedAt.ok) return publish(recordedAt.state);
    const decoded = decodeLegacyClientV1(sourceRead.raw);
    if (decoded.kind !== "ready") {
      const sourceState = decoded.kind === "unsupportedVersion"
        ? unsupportedVersion(decoded.foundVersion)
        : invalid(decoded.reason);
      const quarantineFailure = ensureQuarantine(
        sourceRead.raw,
        sourceState,
        recordedAt.value,
      );
      if (quarantineFailure) return publish(quarantineFailure);
      const receiptFailure = writeReceipt(
        { kind: "absent" },
        { kind: "quarantined", sourceState },
        recordedAt.value,
        "import-receipt-write-failed",
      );
      if (receiptFailure) return publish(receiptFailure);
      const cleanupFailure = cleanupBroadLegacy();
      return publish(cleanupFailure || { kind: "quarantined", sourceState });
    }

    const stageFailure = ensureAuthorStage(decoded.author, recordedAt.value);
    if (stageFailure) return publish(stageFailure);

    let consentState;
    try {
      consentState = readConsentState();
    } catch (_error) {
      return publish({
        kind: "blocked",
        domain: "telemetry-consent",
        state: invalid("legacy-consent-state-read-rejected"),
      });
    }
    if (decoded.telemetry.choice === "disabled") {
      try {
        consentState = await importLegacyConsentWithdrawal({
          choice: "disabled",
          decidedAtUtc: decoded.telemetry.decidedAtUtc,
        });
      } catch (_error) {
        return publish({
          kind: "blocked",
          domain: "telemetry-consent",
          state: invalid("legacy-consent-command-rejected"),
        });
      }
    }
    if (!consentImportSettled(decoded.telemetry.choice, consentState)) {
      return publish({ kind: "blocked", domain: "telemetry-consent", state: consentState });
    }

    let result;
    if (decoded.telemetry.choice === "enabled") {
      const sourceState = Object.freeze({
        kind: "requiresReconsent",
        reason: "legacy-enabled-has-no-versioned-consent",
      });
      const quarantineFailure = ensureQuarantine(
        sourceRead.raw,
        sourceState,
        recordedAt.value,
      );
      if (quarantineFailure) return publish(quarantineFailure);
      result = { kind: "requiresReconsent" };
    } else {
      result = {
        kind: "imported",
        consentRevision: validReadyState(consentState) ? consentState.revision : null,
      };
    }
    const receiptFailure = writeReceipt(
      { kind: "staged" },
      result,
      recordedAt.value,
      "import-receipt-write-failed",
    );
    if (receiptFailure) return publish(receiptFailure);
    const cleanupFailure = cleanupBroadLegacy();
    return publish(cleanupFailure || legacyClientImportReceiptProjection({
      author: { kind: "staged" },
      result,
    }));
  }

  function initialize() {
    if (!commonPending) {
      commonPending = withCrossContextLock(runCommon).finally(() => {
        commonPending = null;
      });
    }
    return commonPending;
  }

  async function runLocalAuthorImport() {
    const common = await runCommon();
    if (common.kind === "missing" || common.kind === "quarantined") {
      return { kind: "missing" };
    }
    if (common.kind !== "complete") return common;

    const receiptRead = read(localStorage, RECEIPT_KEY, "import-receipt-read-failed");
    if (!receiptRead.ok) return publish(receiptRead.state);
    const receipt = decodeLegacyClientImportReceiptV1(receiptRead.raw);
    if (receipt.kind !== "ready") return publish(receipt);
    if (receipt.receipt.author.kind === "imported") {
      const cleanupFailure = remove(
        localStorage,
        AUTHOR_STAGE_KEY,
        "author-stage-remove-failed",
      );
      return publish(cleanupFailure || legacyClientImportReceiptProjection(receipt.receipt));
    }
    if (receipt.receipt.author.kind === "discarded") {
      return { kind: "discarded" };
    }
    if (receipt.receipt.author.kind !== "staged") {
      return { kind: "missing" };
    }

    const stageRead = read(localStorage, AUTHOR_STAGE_KEY, "author-stage-read-failed");
    if (!stageRead.ok) return publish(stageRead.state);
    const stage = decodeLegacyClientAuthorStageV1(stageRead.raw);
    if (stage.kind !== "ready") return publish(stage);

    let authorState;
    try {
      authorState = await importLegacyAuthor(
        stage.stage.author,
        { expectedRevision: null },
      );
    } catch (_error) {
      return publish({
        kind: "blocked",
        domain: "local-author",
        state: invalid("legacy-author-command-rejected"),
      });
    }
    if (!importedAuthorMatchesStage(authorState, stage.stage.author)) {
      return publish({ kind: "blocked", domain: "local-author", state: authorState });
    }

    const recordedAt = timestamp("author-import");
    if (!recordedAt.ok) return publish(recordedAt.state);
    const nextReceipt = {
      ...receipt.receipt,
      author: { kind: "imported", revision: authorState.revision },
      recordedAtUtc: recordedAt.value,
    };
    const receiptFailure = writeReceipt(
      nextReceipt.author,
      nextReceipt.result,
      nextReceipt.recordedAtUtc,
      "author-receipt-write-failed",
    );
    if (receiptFailure) return publish(receiptFailure);
    const removeFailure = remove(
      localStorage,
      AUTHOR_STAGE_KEY,
      "author-stage-remove-failed",
    );
    return publish(removeFailure || legacyClientImportReceiptProjection(nextReceipt));
  }

  async function runDiscardLocalAuthor() {
    const common = await runCommon();
    if (common.kind === "missing" || common.kind === "quarantined") {
      const removeFailure = remove(
        localStorage,
        AUTHOR_STAGE_KEY,
        "author-stage-remove-failed",
      );
      return removeFailure || { kind: "missing" };
    }
    if (common.kind !== "complete") return common;

    const receiptRead = read(localStorage, RECEIPT_KEY, "import-receipt-read-failed");
    if (!receiptRead.ok) return publish(receiptRead.state);
    const receipt = decodeLegacyClientImportReceiptV1(receiptRead.raw);
    if (receipt.kind !== "ready") return publish(receipt);
    if (receipt.receipt.author.kind === "imported") {
      const removeFailure = remove(
        localStorage,
        AUTHOR_STAGE_KEY,
        "author-stage-remove-failed",
      );
      return publish(removeFailure || legacyClientImportReceiptProjection(receipt.receipt));
    }
    if (receipt.receipt.author.kind === "absent") return { kind: "missing" };

    let nextReceipt = receipt.receipt;
    if (receipt.receipt.author.kind === "staged") {
      const recordedAt = timestamp("author-discard");
      if (!recordedAt.ok) return publish(recordedAt.state);
      nextReceipt = {
        ...receipt.receipt,
        author: { kind: "discarded" },
        recordedAtUtc: recordedAt.value,
      };
      const receiptFailure = writeReceipt(
        nextReceipt.author,
        nextReceipt.result,
        nextReceipt.recordedAtUtc,
        "author-receipt-write-failed",
      );
      if (receiptFailure) return publish(receiptFailure);
    }
    const removeFailure = remove(
      localStorage,
      AUTHOR_STAGE_KEY,
      "author-stage-remove-failed",
    );
    if (removeFailure) return publish(removeFailure);
    publish(legacyClientImportReceiptProjection(nextReceipt));
    return { kind: "discarded" };
  }

  function enqueueAuthorMutation(operation) {
    const result = authorMutationTail.then(operation, operation);
    authorMutationTail = result.then(() => undefined, () => undefined);
    return result;
  }

  async function runReset() {
    const failure = remove(localStorage, LEGACY_CLIENT_KEY, "legacy-client-remove-failed")
      || remove(localStorage, AUTHOR_STAGE_KEY, "author-stage-remove-failed")
      || remove(localStorage, QUARANTINE_KEY, "legacy-quarantine-remove-failed")
      || remove(localStorage, RECEIPT_KEY, "import-receipt-remove-failed")
      || remove(sessionStorage, LEGACY_TAB_KEY, "legacy-tab-remove-failed");
    return publish(failure || { kind: "missing" });
  }

  return Object.freeze({
    initialize,
    initializeLocalAuthor: () => enqueueAuthorMutation(
      () => withCrossContextLock(runLocalAuthorImport),
    ),
    discardLocalAuthor: () => enqueueAuthorMutation(
      () => withCrossContextLock(runDiscardLocalAuthor),
    ),
    reset: () => enqueueAuthorMutation(() => withCrossContextLock(runReset)),
    state,
  });
}

let browserImporter = null;

function isCompatibleBrowserImporter(value) {
  return Boolean(
    value
    && value[BROWSER_IMPORT_BRAND] === LEGACY_CLIENT_V1_IMPORT_VERSION
    && typeof value.initialize === "function"
    && typeof value.initializeLocalAuthor === "function"
    && typeof value.discardLocalAuthor === "function"
    && typeof value.reset === "function"
    && typeof value.state === "function",
  );
}

function browserLegacyImporter() {
  const windowRef = globalThis.window;
  const shared = windowRef?.[BROWSER_IMPORT_PROPERTY];
  if (shared !== undefined) {
    if (!isCompatibleBrowserImporter(shared)) {
      throw new TypeError("The shared legacy client import has an incompatible contract.");
    }
    browserImporter = shared;
  }
  if (!browserImporter) {
    let localStorage = null;
    let sessionStorage = null;
    let lockManager = null;
    try {
      localStorage = globalThis.localStorage;
    } catch (_error) {
      localStorage = null;
    }
    try {
      sessionStorage = globalThis.sessionStorage;
    } catch (_error) {
      sessionStorage = null;
    }
    try {
      lockManager = globalThis.navigator?.locks ?? null;
    } catch (_error) {
      lockManager = null;
    }
    const consentRepository = browserLocalPrivacyControlsRepository();
    const importer = createLegacyClientV1Importer({
      localStorage,
      sessionStorage,
      importLegacyAuthor: (identity, options) => (
        browserLocalAuthorRepository().importLegacyIdentity(identity, options)
      ),
      readConsentState: consentRepository.state,
      importLegacyConsentWithdrawal: consentRepository.importLegacyWithdrawal,
      lockManager,
    });
    browserImporter = Object.freeze({
      [BROWSER_IMPORT_BRAND]: LEGACY_CLIENT_V1_IMPORT_VERSION,
      initialize: importer.initialize,
      initializeLocalAuthor: importer.initializeLocalAuthor,
      discardLocalAuthor: importer.discardLocalAuthor,
      reset: importer.reset,
      state: importer.state,
    });
  }
  if (windowRef && typeof windowRef === "object") {
    windowRef[BROWSER_IMPORT_PROPERTY] = browserImporter;
  }
  return browserImporter;
}

export function initializeBrowserUserState() {
  return browserLegacyImporter().initialize();
}

export function initializeBrowserLocalAuthor() {
  return browserLegacyImporter().initializeLocalAuthor();
}

export function discardBrowserLegacyAuthorImport() {
  return browserLegacyImporter().discardLocalAuthor();
}

export function resetBrowserUserStateImport() {
  return browserLegacyImporter().reset();
}

export function browserUserStateImportStatus() {
  return browserImporter?.state?.() ?? Object.freeze({ kind: "uninitialized" });
}
